/**
 * Floor pins for one inspection module on a building.
 * Uses the building's level drawings. Writes that module's assets only.
 * Optional `groups` nests children (e.g. calibration sensors under a chiller).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';
import AuthImage from './AuthImage';
import SectionCard from './SectionCard';

function fitPlanBox(nw, nh, maxW, maxH) {
  if (!nw || !nh || !maxW || !maxH) return { w: 0, h: 0 };
  const s = Math.min(maxW / nw, maxH / nh);
  return {
    w: Math.max(1, Math.round(nw * s)),
    h: Math.max(1, Math.round(nh * s)),
  };
}

function pinOf(asset) {
  if (asset?.pin && asset.pin.x != null && asset.pin.y != null) return asset.pin;
  if (asset?.pin_x != null && asset?.pin_y != null) {
    return { x: Number(asset.pin_x), y: Number(asset.pin_y), level_id: asset.level_id || null };
  }
  return null;
}

function assetLabel(asset) {
  return asset.short_label || asset.asset_tag || asset.name || `Asset ${asset.id}`;
}

function isActiveRow(row) {
  return String(row?.status || 'ACTIVE').toUpperCase() === 'ACTIVE';
}

export default function InspectionFloorPlan({
  buildingId,
  moduleCode,
  assets = [],
  /** Nested parents with optional `children` (calibration: chiller → sensors). */
  groups = null,
  preferredLevelId = '',
  groupLabel = '',
  /** When true, Place stays on the group's saved level (browse others only via group Save). */
  lockPreferredLevel = false,
  /**
   * Calibration: chiller (parent) rows are locked — only nested sensors (DS1/MS1…)
   * can be selected and pinned on the drawing.
   */
  placeChildrenOnly = false,
  /** Override default asset pin PATCH — used for calibration sensor pins. */
  onSavePin,
  /** Hide Edit / Disable / Remove (legacy sensor-only place mode). */
  placeOnly = false,
  emptyLabel = 'No assets yet.',
  helpText = '',
  onLevelChange,
  onSaved,
  onEdit,
  onSetEnabled,
  onRemove,
  onOpenChecklist,
}) {
  const imgRef = useRef(null);
  const hostRef = useRef(null);
  const naturalRef = useRef({ w: 0, h: 0 });
  const [levelId, setLevelId] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [drag, setDrag] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [scale, setScale] = useState(1);
  const [planBox, setPlanBox] = useState({ w: 0, h: 0 });
  const [planFailed, setPlanFailed] = useState(false);

  const { data: setup, isLoading } = useQuery({
    queryKey: ['building', buildingId, 'levels-setup', 'inspection-module'],
    queryFn: async () => (await api.get(`/buildings/${buildingId}/levels-setup`)).data?.data,
    enabled: !!buildingId,
  });

  const levels = useMemo(() => (Array.isArray(setup?.levels) ? setup.levels : []), [setup]);

  useEffect(() => {
    if (!levels.length) {
      setLevelId('');
      return;
    }
    if (
      preferredLevelId &&
      levels.some((level) => String(level.id) === String(preferredLevelId))
    ) {
      if (String(levelId) !== String(preferredLevelId)) {
        setLevelId(String(preferredLevelId));
      }
      return;
    }
    if (!levels.some((level) => String(level.id) === String(levelId))) {
      setLevelId(String(levels[0].id));
    }
  }, [levels, levelId, preferredLevelId]);

  const selectedLevel = levels.find((level) => String(level.id) === String(levelId));
  const planUrl = useMemo(() => {
    if (selectedLevel?.plan_url) return selectedLevel.plan_url;
    const plans = Array.isArray(setup?.plans) ? setup.plans : [];
    const planId = selectedLevel?.floor_plan_id;
    if (planId) {
      const plan = plans.find((row) => String(row.id) === String(planId));
      if (plan?.plan_url) return plan.plan_url;
    }
    const levelCount = Array.isArray(setup?.levels) ? setup.levels.length : 0;
    if (levelCount > 1) return '';
    return setup?.building?.floor_plan_url || '';
  }, [selectedLevel, setup]);

  const recomputePlanBox = useCallback(() => {
    const { w: nw, h: nh } = naturalRef.current;
    if (!nw || !nh) return;
    const host = hostRef.current;
    const maxW = Math.max(280, (host?.clientWidth || 640) - 8);
    const maxH = Math.max(320, host?.clientHeight || Math.min(window.innerHeight * 0.7, 900));
    setPlanBox(fitPlanBox(nw, nh, maxW, maxH));
  }, []);

  useEffect(() => {
    setScale(1);
    setPlanFailed(false);
    naturalRef.current = { w: 0, h: 0 };
    setPlanBox({ w: 0, h: 0 });
  }, [planUrl]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => recomputePlanBox());
    ro.observe(host);
    window.addEventListener('resize', recomputePlanBox);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recomputePlanBox);
    };
  }, [recomputePlanBox, planUrl]);

  const useGroups = Array.isArray(groups);

  function isSensorRow(row) {
    return row?.sensor_id != null && row.sensor_id !== '';
  }

  /** Parent chiller is locked on calibration floor; only sensors get a place pin. */
  function canPlaceRow(row) {
    if (!placeChildrenOnly) return true;
    return isSensorRow(row);
  }

  /** Flat list of every row (parents + children when grouped). */
  const listed = useMemo(() => {
    if (useGroups) {
      const flat = [];
      for (const parent of groups) {
        flat.push(parent);
        for (const child of parent.children || []) flat.push(child);
      }
      return flat;
    }
    return [...assets].sort((a, b) => {
      const rank = (row) => (isActiveRow(row) ? 0 : 1);
      return rank(a) - rank(b);
    });
  }, [assets, groups, useGroups]);

  useEffect(() => {
    if (!placeChildrenOnly || !selectedId) return;
    const row = listed.find((asset) => String(asset.id) === String(selectedId));
    if (row && !(row.sensor_id != null && row.sensor_id !== '')) setSelectedId(null);
  }, [placeChildrenOnly, selectedId, listed]);

  const onLevel = listed.filter((asset) => {
    if (!isActiveRow(asset)) return false;
    if (!canPlaceRow(asset)) return false;
    const pin = drag?.id === asset.id ? drag : pinOf(asset);
    return pin && String(pin.level_id || '') === String(levelId);
  });

  async function savePin(assetId, x, y, nextLevelId) {
    setSavingId(assetId);
    try {
      const item = listed.find((row) => String(row.id) === String(assetId));
      if (onSavePin) {
        await onSavePin(item || { id: assetId }, {
          pin_x: x,
          pin_y: y,
          level_id: nextLevelId,
        });
      } else {
        await api.patch(`/inspection-modules/${moduleCode}/buildings/${buildingId}/assets/${assetId}`, {
          pin_x: x,
          pin_y: y,
          level_id: nextLevelId,
        });
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save pin');
    } finally {
      setSavingId(null);
      setDrag(null);
    }
  }

  function pointFromEvent(event) {
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return null;
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    return {
      x: Math.min(100, Math.max(0, x)),
      y: Math.min(100, Math.max(0, y)),
    };
  }

  function onSheetClick(event) {
    if (event.target?.closest?.('[data-pin]')) return;
    if (!selectedId || !planUrl) return;
    const selected = listed.find((asset) => String(asset.id) === String(selectedId));
    if (!selected || !isActiveRow(selected) || !canPlaceRow(selected)) return;
    const point = pointFromEvent(event);
    if (!point) return;
    savePin(selectedId, point.x, point.y, levelId);
  }

  function onPinPointerDown(event, asset) {
    event.preventDefault();
    event.stopPropagation();
    if (!canPlaceRow(asset)) return;
    const pin = pinOf(asset);
    setSelectedId(asset.id);
    setDrag({ id: asset.id, x: pin?.x ?? 0, y: pin?.y ?? 0, level_id: levelId });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function onPinPointerMove(event) {
    if (!drag) return;
    const point = pointFromEvent(event);
    if (!point) return;
    setDrag((prev) => (prev ? { ...prev, ...point } : prev));
  }

  function onPinPointerUp() {
    if (!drag) return;
    savePin(drag.id, drag.x, drag.y, levelId);
  }

  function renderRowActions(row, { nested = false } = {}) {
    const active = isActiveRow(row);
    const editTarget = row._asset || row;
    return (
      <div className={`mt-1 flex flex-wrap gap-x-3 gap-y-1 ${nested ? 'pl-0' : ''}`}>
        {active && (
          <button
            type="button"
            className="text-xs font-semibold text-gray-700 hover:underline"
            onClick={() => setSelectedId(row.id)}
          >
            Place
          </button>
        )}
        {!placeOnly && (
          <>
            <button
              type="button"
              className="text-xs font-semibold text-primary-700 hover:underline"
              onClick={() => onEdit?.(editTarget, row)}
            >
              Edit
            </button>
            {active ? (
              <button
                type="button"
                className="text-xs font-semibold text-red-600 hover:underline"
                onClick={() => onSetEnabled?.(editTarget, false, row)}
              >
                Disable
              </button>
            ) : (
              <button
                type="button"
                className="text-xs font-semibold text-emerald-700 hover:underline"
                onClick={() => onSetEnabled?.(editTarget, true, row)}
              >
                Enable
              </button>
            )}
            <button
              type="button"
              className="text-xs font-semibold text-red-700 hover:underline"
              onClick={() => onRemove?.(editTarget, row)}
            >
              Remove
            </button>
          </>
        )}
      </div>
    );
  }

  function renderFlatRow(asset) {
    const pin = pinOf(asset);
    const active = isActiveRow(asset);
    const here = pin && String(pin.level_id || '') === String(levelId);
    return (
      <div
        key={asset.id}
        className={`rounded border border-navy-200 px-2 py-1.5 text-sm ${
          !active
            ? 'text-gray-400'
            : String(selectedId) === String(asset.id)
              ? 'border-primary-600'
              : ''
        }`}
      >
        <button
          type="button"
          className="w-full text-left"
          onClick={() => {
            if (placeOnly) {
              if (active) setSelectedId(asset.id);
              return;
            }
            onOpenChecklist?.(asset);
          }}
        >
          <span className="font-semibold">{assetLabel(asset)}</span>
          <span className="block text-xs text-gray-500">
            {!active ? 'Disabled' : here ? 'On this place' : pin ? 'On another place' : 'Not placed'}
            {!placeOnly && Array.isArray(asset.checklist_packs)
              ? ` · ${asset.checklist_packs.join(' ')}`
              : ''}
            {asset.sensor_role ? ` · ${asset.sensor_role}` : ''}
          </span>
        </button>
        {renderRowActions(asset)}
      </div>
    );
  }

  function renderGroup(parent) {
    const active = isActiveRow(parent);
    const children = Array.isArray(parent.children) ? parent.children : [];
    const summary = parent.sensor_summary || children.map((c) => c.short_label || assetLabel(c)).join(' ');
    const parentLocked = placeChildrenOnly;
    return (
      <div key={parent.id} className="space-y-1">
        <div
          className={`rounded border px-2 py-1.5 text-sm ${
            parentLocked
              ? 'border-slate-200 bg-slate-50 text-slate-700'
              : !active
                ? 'border-navy-200 text-gray-400'
                : String(selectedId) === String(parent.id)
                  ? 'border-primary-600 border-navy-200'
                  : 'border-navy-200'
          }`}
        >
          <button
            type="button"
            className="w-full text-left"
            onClick={() => {
              if (parentLocked) {
                // Chiller place is locked on Calibration — pick a sensor below.
                return;
              }
              if (placeOnly) {
                if (active) setSelectedId(parent.id);
                return;
              }
              onOpenChecklist?.(parent._asset || parent);
            }}
          >
            <span className="font-semibold">{parent.name || assetLabel(parent)}</span>
            <span className="block text-xs text-gray-500">
              {parentLocked
                ? `Chiller place locked · place sensors only${summary ? ` · ${summary}` : ''}`
                : !active
                  ? 'Disabled'
                  : pinOf(parent) && String(pinOf(parent).level_id || '') === String(levelId)
                    ? 'On this place'
                    : pinOf(parent)
                      ? 'On another place'
                      : 'Not placed'}
              {!parentLocked && summary ? ` · ${summary}` : ''}
            </span>
          </button>
          {!parentLocked && renderRowActions(parent)}
        </div>
        {children.map((child) => {
          const childPin = pinOf(child);
          const childActive = isActiveRow(child);
          const childHere = childPin && String(childPin.level_id || '') === String(levelId);
          return (
            <div
              key={child.id}
              className={`ml-3 rounded border border-navy-100 bg-green-50/50 px-2 py-1.5 text-sm ${
                !childActive
                  ? 'text-gray-400'
                  : String(selectedId) === String(child.id)
                    ? 'border-primary-600'
                    : ''
              }`}
            >
              <button
                type="button"
                className="w-full text-left"
                onClick={() => {
                  if (childActive) setSelectedId(child.id);
                }}
              >
                <span className="font-semibold">
                  {child.short_label ? (
                    <>
                      <span className="tabular-nums mr-1">{child.short_label}</span>
                      {child.name || ''}
                    </>
                  ) : (
                    assetLabel(child)
                  )}
                </span>
                <span className="block text-xs text-gray-500">
                  {!childActive
                    ? 'Disabled'
                    : childHere
                      ? 'On this place'
                      : childPin
                        ? 'On another place'
                        : 'Not placed'}
                  {child.sensor_role
                    ? ` · ${child.sensor_role === 'door' ? 'Door' : 'Monitoring'}`
                    : ''}
                </span>
              </button>
              {renderRowActions(child, { nested: true })}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <SectionCard
      title={groupLabel ? `Floor plan · ${groupLabel}` : 'Floor plan'}
      actions={
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
          Place
          <select
            className="border border-navy-200 rounded px-2 py-1.5 min-w-[12rem] font-normal"
            value={levelId}
            disabled={lockPreferredLevel && !!preferredLevelId}
            title={
              lockPreferredLevel
                ? 'Place is set on the chiller group (Save name / Place). Changing it here is disabled so groups stay separate.'
                : undefined
            }
            onChange={(e) => {
              const next = e.target.value;
              setLevelId(next);
              onLevelChange?.(next);
            }}
          >
            {levels.map((level) => (
              <option key={level.id} value={level.id}>
                {[level.block_label, level.label || level.code].filter(Boolean).join(' · ') || level.id}
              </option>
            ))}
          </select>
        </label>
      }
    >

      {isLoading && <p className="text-sm text-gray-500">Loading drawings…</p>}
      {!isLoading && !planUrl && (
        <p className="text-sm text-gray-600">
          No floor drawing for this place yet.{' '}
          <Link to={`/buildings/${buildingId}/edit`} className="font-semibold text-primary-700 underline">
            Edit Building
          </Link>{' '}
          to upload one.
        </p>
      )}

      {!!helpText && <p className="text-xs text-gray-500 mb-2 px-1">{helpText}</p>}

      {!!planUrl && (
        <div className="grid gap-3 items-start lg:grid-cols-[16rem_1fr]">
          <div className="space-y-1 max-h-[70vh] overflow-auto">
            {useGroups
              ? groups.map((parent) => renderGroup(parent))
              : listed.map((asset) => renderFlatRow(asset))}
            {useGroups
              ? !groups.length && <p className="text-sm text-gray-500">{emptyLabel}</p>
              : !listed.length && <p className="text-sm text-gray-500">{emptyLabel}</p>}
            {selectedId && (
              <button
                type="button"
                className="text-xs text-red-600 px-1 py-1"
                onClick={() => savePin(selectedId, null, null, null)}
              >
                Remove pin
              </button>
            )}
          </div>
          <div
            className="rounded border border-navy-200"
            onPointerMove={onPinPointerMove}
            onPointerUp={onPinPointerUp}
          >
            <div className="flex justify-start gap-1 p-2">
              <button type="button" className="px-2 py-1 text-xs border border-navy-200 rounded" onClick={() => setScale((n) => Math.max(1, Number((n - 0.25).toFixed(2))))}>
                −
              </button>
              <button type="button" className="px-2 py-1 text-xs border border-navy-200 rounded" onClick={() => setScale((n) => Math.min(3, Number((n + 0.25).toFixed(2))))}>
                +
              </button>
            </div>
            <div ref={hostRef} className="relative h-[70vh] min-h-[420px] overflow-auto">
            {planFailed && (
              <p className="m-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                The drawing file could not be loaded. Upload it again in Edit Building.
              </p>
            )}
            <div
              ref={imgRef}
              className="relative"
              onClick={onSheetClick}
              style={{
                width: planBox.w ? planBox.w * scale : '100%',
                height: planBox.h ? planBox.h * scale : '70vh',
              }}
            >
              <AuthImage
                filename={planUrl}
                alt="Floor drawing"
                draggable={false}
                onError={() => setPlanFailed(true)}
                onLoad={(event) => {
                  const img = event.currentTarget;
                  naturalRef.current = { w: img.naturalWidth || 0, h: img.naturalHeight || 0 };
                  recomputePlanBox();
                }}
                className="block w-full h-full select-none pointer-events-none"
              />
              {onLevel.map((asset) => {
                const pin = drag?.id === asset.id ? drag : pinOf(asset);
                if (!pin) return null;
                return (
                  <button
                    key={asset.id}
                    type="button"
                    data-pin
                    className="absolute z-10 -translate-x-1/2 -translate-y-1/2 px-1.5 py-0.5 text-xs font-bold border-2 border-primary-600 bg-green-100 text-primary-800 shadow cursor-grab"
                    style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                    onPointerDown={(event) => onPinPointerDown(event, asset)}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {savingId === asset.id ? '…' : assetLabel(asset)}
                  </button>
                );
              })}
            </div>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
